import { ArrowLeft, CheckCircle2, Crown, LifeBuoy, ShieldCheck } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { createVipOrder, getVipPlans } from '../api.js'
import { useAuth } from '../auth-context.js'
import MembershipStatusCard from '../components/MembershipStatusCard.jsx'
import { NOINDEX_ROBOTS, setPageMetadata } from '../page-metadata.js'

const BENEFITS = [
  ['简历分析与优化', '每月 3 次简历分析', '每日 30 次简历分析与优化'],
  ['GitHub 项目分析', '每月 10 次 GitHub 项目分析', '每日 20 次 GitHub 项目分析'],
  ['AI 聊天', '每月 20 次 AI 聊天', '每日更高 AI 聊天额度'],
  ['简历导出', '每月 1 次简历导出', '更多导出次数'],
  ['项目表达与面试', '基础建议', '更完整的项目表达和面试建议'],
]

function VipPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busyPlan, setBusyPlan] = useState('')

  useEffect(() => setPageMetadata({
    title: 'VIP 权益 - 青途 AI',
    description: '早鸟体验价 VIP，适合学生项目分析、简历优化和面试准备。',
    robots: NOINDEX_ROBOTS,
  }), [])

  useEffect(() => {
    const controller = new AbortController()
    getVipPlans(controller.signal)
      .then((result) => setPlans(result?.plans || []))
      .catch((loadError) => {
        if (loadError.name !== 'AbortError') setError(loadError.message || 'VIP 套餐加载失败')
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  const buyPlan = useCallback(async (planId) => {
    if (!user?.id) {
      navigate('/login')
      return
    }
    setBusyPlan(planId)
    setError('')
    try {
      const result = await createVipOrder(planId)
      const orderNo = result?.order?.orderNo
      if (orderNo) navigate(`/vip/orders/${orderNo}`)
    } catch (buyError) {
      setError(buyError.message || '订单创建失败，请稍后重试')
    } finally {
      setBusyPlan('')
    }
  }, [navigate, user?.id])

  return (
    <div className="page-container vip-page">
      <Link className="secondary-button vip-back" to="/">
        <ArrowLeft size={16} />
        返回首页
      </Link>

      <section className="page-heading compact-heading vip-heading">
        <div>
          <span className="eyebrow">VIP MEMBERSHIP</span>
          <h1>早鸟体验价 VIP</h1>
          <p>适合学生项目分析、简历优化和面试准备。免费版提供每月额度，VIP 额度每日刷新。付款后人工审核开通，通常 24 小时内处理。</p>
        </div>
        <span className="profile-role-badge is-admin">
          <Crown size={15} />
          月卡 4.9 元起
        </span>
      </section>

      <MembershipStatusCard />

      {error && <div className="admin-notice error" role="alert">{error}</div>}

      <section className="vip-plan-grid">
        <article className="profile-card vip-free-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><ShieldCheck size={20} /></span>
            <div>
              <h2>免费版</h2>
              <p>保留基础体验，适合偶尔查看趋势和尝试分析。</p>
            </div>
          </header>
          <strong className="vip-plan-price">¥0</strong>
          <ul className="vip-plan-list">
            <li>基础项目分析额度</li>
            <li>基础简历分析额度</li>
            <li>AI Pet 基础陪伴</li>
          </ul>
        </article>

        {loading ? (
          <article className="profile-card"><p className="profile-muted">正在加载套餐...</p></article>
        ) : plans.map((plan) => (
          <article className="profile-card vip-plan-card" key={plan.id}>
            <header className="profile-card-heading">
              <span className="profile-card-icon"><Crown size={20} /></span>
              <div>
                <h2>{plan.name}</h2>
                <p>{plan.description}</p>
              </div>
            </header>
            <strong className="vip-plan-price">
              ¥{plan.priceYuan}
              <small> / {plan.durationDays} 天</small>
            </strong>
            <span className="vip-early-badge">早鸟体验价</span>
            <button
              className="primary-button profile-card-button"
              type="button"
              disabled={busyPlan === plan.id}
              onClick={() => buyPlan(plan.id)}
            >
              <Crown size={16} />
              {busyPlan === plan.id ? '正在创建订单' : '立即购买'}
            </button>
          </article>
        ))}
      </section>

      <section className="profile-card vip-benefits-card">
        <header className="profile-card-heading">
          <span className="profile-card-icon"><CheckCircle2 size={20} /></span>
          <div>
            <h2>权益对比</h2>
          <p>每月免费额度用完后，可升级 VIP 获得每日刷新额度和更完整的学生成长分析链路。</p>
          </div>
        </header>
        <div className="vip-benefits-table">
          <div className="vip-benefits-row is-head">
            <strong>权益</strong>
            <strong>免费版</strong>
            <strong>VIP</strong>
          </div>
          {BENEFITS.map(([name, free, vip]) => (
            <div className="vip-benefits-row" key={name}>
              <span>{name}</span>
              <span>{free}</span>
              <span><CheckCircle2 size={15} />{vip}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="profile-card vip-support-card">
        <header className="profile-card-heading">
          <span className="profile-card-icon"><LifeBuoy size={20} /></span>
          <div>
            <h2>客服与退款说明</h2>
            <p>付款后通常会在 24 小时内人工审核开通。如遇到开通失败、重复付款、功能异常或需要退款，请通过客服与反馈页面联系我们。</p>
          </div>
        </header>
        <Link className="secondary-button profile-card-button" to="/support">
          <LifeBuoy size={16} />
          联系客服与反馈
        </Link>
      </section>
    </div>
  )
}

export default VipPage
